import { Injectable } from '@angular/core';

/**
 * The fixed pp-delta bands offered in the Change-column header filter. Absolute pp. Originally the Story
 * 4.4 band chips; retained as the value type for the native header-filter items after the toolbar Status
 * chips + Discount Delta range were retired in favour of column header filters.
 */
export type DeltaBand = '0-5' | '5-10' | '10-20' | '>20';

/**
 * The Accepted-tab date-filter preset (Story 4.6, BR-13). `null` = no date filter applied.
 * `custom` pairs with `dateFrom` / `dateTo` for an explicit inclusive range.
 */
export type DatePreset = 'last7' | 'last30' | 'thisMonth' | 'lastMonth' | 'custom' | null;

/**
 * Session-scoped holder for the Customer Pricing filter state — the Story 4.2 supplier selection and the
 * Story 4.6 Accepted-tab date filter (BR-40). The Story 4.4 status chips + Story 4.5 Discount Delta range
 * that used to live here were retired in favour of native DevExtreme column header filters, whose state the
 * grid itself owns — so this service no longer tracks them.
 *
 * Provided in root, so a single instance lives for the lifetime of the loaded app. This gives the
 * exact persistence BR-40 asks for:
 *  - the selection PERSISTS across tab switches (the component reads it on each load) and across
 *    leaving + re-entering the dashboard route without a reload (the root singleton outlives the
 *    component);
 *  - the selection RESETS on a full page reload — the JS heap is wiped and the service is
 *    re-instantiated with the defaults.
 *
 * Deliberately NOT `sessionStorage`/`localStorage`: `sessionStorage` survives a full reload (it lives
 * for the whole tab session), which would violate BR-40 [[feedback_promoflo_br40_sort_persistence]].
 *
 * Holds:
 *  - `supplierCode` — the supplier filter identity (Story 4.2), or `null` = the "All" pill (no filter);
 *  - `datePreset` + `dateFrom` / `dateTo` — the Accepted-tab date filter (Story 4.6, BR-13). UNLIKE the
 *    supplier filter, this one is TAB-LOCAL: it is only ever applied + rendered on the Accepted tab and
 *    "does not propagate" to Pending Review / Update Requests (BR-40). It is held here purely so the
 *    selection survives a tab round-trip / route re-entry and resets on a full page reload — same in-memory
 *    posture as the supplier filter (never local/sessionStorage).
 */
@Injectable({ providedIn: 'root' })
export class CustomerPricingFilterService {
  /** Selected supplier_code, or null for "All". In-memory only (resets on full page reload, BR-40). */
  supplierCode: string | null = null;

  /**
   * The active Accepted-tab date-filter preset (Story 4.6), or `null` when no date filter is applied.
   * TAB-LOCAL (BR-40) — only ever applied/rendered on the Accepted tab. In-memory only (resets on reload).
   */
  datePreset: DatePreset = null;

  /**
   * The custom-range bounds (Story 4.6) as `yyyy-MM-dd` date-only strings, used only when
   * `datePreset === 'custom'`. A `null` bound is open-ended (no lower / no upper bound respectively).
   */
  dateFrom: string | null = null;
  dateTo: string | null = null;

  /** Clear the Story 4.6 Accepted-tab date filter (preset + custom range). Leaves the supplier filter untouched. */
  clearDateFilter(): void {
    this.datePreset = null;
    this.dateFrom = null;
    this.dateTo = null;
  }
}
